/**
 * Controles de formulaire. Chaque controle est relie a son libelle, son aide et
 * son erreur : un lecteur d'ecran annonce tout ce qui est affiche.
 */
import { useId, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { flushSync } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { useRepliDetails } from './menus';

/**
 * Apres un refus (controle local ou reponse de l'API) : `appliquer` pose les
 * messages, puis le premier champ en erreur, dans l'ordre du formulaire, prend
 * le focus. Son message, qui le decrit, est alors lu ; il doit donc etre dans
 * le DOM avant le focus, d'ou `flushSync`.
 */
export function signalerErreursDeChamps(
  appliquer: () => void,
  champs: ReadonlyArray<readonly [erreur: string | null | undefined, ref: RefObject<HTMLElement | null>]>,
): void {
  flushSync(appliquer);
  champs.find(([erreur]) => erreur)?.[1].current?.focus();
}

/** Etiquette + champ correctement associes (`label for` / `id`), aide et erreur. */
export function Champ({
  libelle,
  aide,
  erreur,
  requis = false,
  children,
  className = '',
}: {
  libelle: string;
  aide?: ReactNode;
  erreur?: string | null;
  requis?: boolean;
  /** Recoit l'identifiant a poser sur le controle, et celui de sa description. */
  children: (id: string, decritPar: string | undefined) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const idAide = aide ? `${id}-aide` : undefined;
  const idErreur = erreur ? `${id}-erreur` : undefined;
  const decritPar = [idErreur, idAide].filter(Boolean).join(' ') || undefined;

  return (
    <div className={className}>
      <label className="etiquette" htmlFor={id}>
        {libelle}
        {requis && (
          <span className="ml-0.5 text-danger-600" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children(id, decritPar)}
      {erreur && (
        <p id={idErreur} className="mt-1.5 text-xs font-medium text-danger-700">
          {erreur}
        </p>
      )}
      {aide && (
        <p id={idAide} className="aide">
          {aide}
        </p>
      )}
    </div>
  );
}

/** Case a cocher avec libelle et explication, alignes sur la meme grille. */
export function CaseACocher({
  libelle,
  description,
  coche,
  onChange,
  desactive = false,
  className = '',
}: {
  libelle: ReactNode;
  description?: ReactNode;
  coche: boolean;
  onChange: (coche: boolean) => void;
  desactive?: boolean;
  className?: string;
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-2.5 text-base text-encre-douce ${
        desactive ? 'cursor-not-allowed opacity-60' : ''
      } ${className}`}
    >
      <input
        type="checkbox"
        className="case mt-0.5"
        checked={coche}
        disabled={desactive}
        onChange={(evenement) => onChange(evenement.target.checked)}
      />
      <span className="min-w-0">
        <span className="block text-encre">{libelle}</span>
        {description && <span className="mt-0.5 block text-sm text-encre-discrete">{description}</span>}
      </span>
    </label>
  );
}

export interface OptionMulti {
  valeur: string;
  libelle: string;
  indice?: string;
  couleur?: string;
}

/**
 * Selection multiple sous forme de liste de cases a cocher repliee dans un
 * `<details>` : nativement accessible au clavier, sans piege de focus. Elle se
 * referme comme un menu (Echap, clic exterieur) et des que le focus la quitte :
 * ouverte, elle recouvrirait les champs suivants.
 */
export function SelecteurMulti({
  libelle,
  options,
  valeurs,
  onChange,
  placeholder = 'Tous',
  recherche = false,
}: {
  libelle: string;
  options: readonly OptionMulti[];
  valeurs: readonly string[];
  onChange: (valeurs: string[]) => void;
  placeholder?: string;
  recherche?: boolean;
}) {
  const id = useId();
  const refDetails = useRef<HTMLDetailsElement>(null);
  useRepliDetails(refDetails, { focusExterieur: true });
  const selection = new Set(valeurs);
  const resume =
    valeurs.length === 0
      ? placeholder
      : valeurs.length <= 2
        ? options
            .filter((option) => selection.has(option.valeur))
            .map((option) => option.libelle)
            .join(', ') || `${valeurs.length} sélectionnés`
        : `${valeurs.length} sélectionnés`;

  const basculer = (valeur: string): void => {
    const suivante = new Set(selection);
    if (suivante.has(valeur)) suivante.delete(valeur);
    else suivante.add(valeur);
    onChange(options.filter((option) => suivante.has(option.valeur)).map((option) => option.valeur));
  };

  return (
    <div>
      <span className="etiquette" id={`${id}-libelle`}>
        {libelle}
      </span>
      <details ref={refDetails} className="group relative">
        <summary
          aria-labelledby={`${id}-libelle`}
          className="champ flex cursor-pointer list-none items-center justify-between gap-2 marker:content-none"
        >
          <span className={valeurs.length === 0 ? 'truncate text-encre-discrete' : 'truncate'}>
            {resume}
          </span>
          <ChevronDown
            aria-hidden="true"
            className="shrink-0 text-encre-tenue transition-transform group-open:rotate-180"
          />
        </summary>
        <div className="surface-menu absolute z-20 mt-1 max-h-72 w-full min-w-56 overflow-auto p-1.5">
          {recherche && (
            <input
              type="search"
              aria-label={`Rechercher dans ${libelle}`}
              placeholder="Filtrer la liste…"
              className="champ mb-1.5 h-8"
              onChange={(evenement) => {
                const terme = evenement.target.value.toLowerCase();
                const conteneur = evenement.target.parentElement;
                if (!conteneur) return;
                for (const element of conteneur.querySelectorAll<HTMLElement>('[data-option]')) {
                  const texte = element.dataset['option'] ?? '';
                  element.hidden = terme !== '' && !texte.includes(terme);
                }
              }}
            />
          )}
          {options.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-encre-discrete">Aucune option.</p>
          )}
          {options.map((option) => (
            <label
              key={option.valeur}
              data-option={`${option.libelle} ${option.valeur} ${option.indice ?? ''}`.toLowerCase()}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-base hover:bg-surface-appuyee"
            >
              <input
                type="checkbox"
                className="case h-3.5 w-3.5"
                checked={selection.has(option.valeur)}
                onChange={() => basculer(option.valeur)}
              />
              {option.couleur && (
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: option.couleur }}
                />
              )}
              <span className="truncate">{option.libelle}</span>
              {option.indice && (
                <span className="ml-auto shrink-0 text-2xs text-encre-discrete">{option.indice}</span>
              )}
            </label>
          ))}
          {valeurs.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full rounded-sm px-2 py-1 text-left text-sm font-medium text-accent-700 hover:bg-accent-50"
            >
              Tout désélectionner
            </button>
          )}
        </div>
      </details>
    </div>
  );
}
